import Highcharts from 'highcharts';
import theme from '../../../theme';
import chartColors from './chartColors';
import settings from '../../../settings';

type ChartOptionsType = {
  chart: Highcharts.ChartOptions;
  lang: Highcharts.LangOptions;
  boost: Highcharts.BoostOptions;
  navigation: Highcharts.NavigationOptions;
  exporting: Highcharts.ExportingOptions;
  yAxis: Highcharts.AxisOptions;
  xAxis: Highcharts.AxisOptions;
  credits: Highcharts.CreditsOptions;
  legend: Highcharts.LegendOptions;
  title: Highcharts.TitleOptions;
  subtitle: Highcharts.SubtitleOptions;
  tooltip: Highcharts.TooltipOptions;
  colors: Array<string>;
  plotOptions: Highcharts.PlotOptions;
};

export function tooltipPositioner(this: Highcharts.Tooltip, labelWidth: number, labelHeight: number) {
  const { chart } = this;
  const { chartWidth } = chart;
  const chartTop = chart.plotTop;

  // Position the tooltip at the top-right
  const x = chartWidth - labelWidth - 10;
  const y = chartTop - 30;

  return { x, y };
}

const chartOptionsTlk: ChartOptionsType = {
  chart: {
    zooming: {
      type: 'xy',
    },
    displayErrors: false,
    backgroundColor: theme.palette.common.white,
    style: {
      color: theme.palette.common.white,
      fontFamily: theme.typography.fontFamilyPlots,
      fontSize: theme.typography.fontSize.toString(),
    },
  },
  lang: {
    numericSymbols: ['', ' e6', ' e9', ' e12'],
  },
  boost: {
    enabled: true,
    useGPUTranslations: true,
    usePreallocated: true,
    debug: {
      timeSetup: false,
      timeSeriesProcessing: false,
      timeKDTree: false,
      showSkipSummary: false,
    },
  },
  navigation: {
    menuItemStyle: {
      fontSize: '12px',
      fontWeight: 'normal',
    },
  },
  exporting: {
    enabled: true,
    buttons: {
      contextButton: {
        menuItems: ['downloadPNG', 'downloadPDF', 'separator', 'downloadCSV', 'downloadXLS'],
        x: -25,
        y: 2,
        verticalAlign: 'bottom',
      },
    },
  },
  xAxis: {
    title: {
      style: {
        fontSize: theme.typography.fontSize.toString(),
      },
    },
    labels: {
      style: {
        fontSize: theme.typography.fontSize.toString(),
      },
    },
    dateTimeLabelFormats: {
      month: '%b',
    },
  },
  yAxis: {
    title: {
      style: {
        fontSize: theme.typography.fontSize.toString(),
      },
    },
    labels: {
      style: {
        fontSize: theme.typography.fontSize.toString(),
      },
    },
  },
  credits: {
    enabled: false,
  },
  legend: {
    layout: 'horizontal',
    align: 'center',
    verticalAlign: 'bottom',
    x: 0,
    y: 0,
    borderWidth: 0,
    itemStyle: {
      fontSize: theme.typography.fontSize.toString(),
      fontWeight: 'bold',
    },
  },
  colors: [
    chartColors.BLUE,
    chartColors.RED,
    chartColors.GREEN,
    chartColors.LILAC,
    chartColors.ORANGE,
    chartColors.DARKGREY,
    chartColors.AQUA,
    chartColors.YELLOW,
    chartColors.PURPLE,
  ],
  plotOptions: {
    series: {
      cursor: 'pointer',
      marker: {
        enabled: false,
      },
      // threshold are set high, to disable boost for most charts, because boost enables some css
      turboThreshold: 1e9,
      // number points, where highcharts stops checking data points format
      boostThreshold: 1e9, // number points, where series enters boost mode
    },
    line: {
      lineWidth: 1,
    },
    pie: {
      dataLabels: {
        enabled: true,
        style: {
          fontSize: theme.typography.fontSize.toString(),
        },
      },
    },
    solidgauge: {
      dataLabels: {
        y: -40,
        borderWidth: 0,
        useHTML: true,
      },
    },
  },
  title: {
    style: {
      fontSize: theme.typography.fontSize.toString(),
      fontWeight: '400',
    },
  },
  subtitle: {
    style: {
      fontSize: theme.typography.fontSize.toString(),
      fontWeight: '400',
    },
  },
  tooltip: {
    // `outside` tooltips live in a body-level container; keep them above MUI dialogs.
    style: { zIndex: theme.zIndex.modal + 1 },
    dateTimeLabelFormats: {
      millisecond: '%A, %e %b %Y, %H:%M:%S.%L',
      second: '%A, %e %b %Y, %H:%M:%S',
      minute: '%A, %e %b %Y, %H:%M',
      hour: '%A, %e %b %Y, %H:%M',
      day: '%A, %e %b %Y',
      week: 'Week from %A, %e %b %Y',
      month: '%B %Y',
      year: '%Y',
    },
  },
};

const chartOptionsAerosim: ChartOptionsType = {
  ...chartOptionsTlk,
  colors: [chartColors.BLUE, chartColors.GREY, chartColors.LIGHTBLUE],
};

const getChartOptions = () => {
  if (settings.product === 'aerosim') {
    return chartOptionsAerosim;
  }

  return chartOptionsTlk;
};

const chartOptions: ChartOptionsType = getChartOptions();
export default chartOptions;
